/**
 * Release gate for the workspace settings area (W-25,
 * docs/frontend-workspace-settings-proposal.md §3 D-13 in the SVN repo).
 *
 * The area ships on the app's own ReleaseStage ladder rather than a bespoke flag:
 *
 *   Alpha -> isInternal                     Inistate staff only. Same test
 *                                           FTSAutomationActionModal uses to hide
 *                                           ReleaseStage.ALPHA actions.
 *   Beta  -> isBeta || workspaceBeta        Consultants/Administrators, plus any
 *                                           workspace with Beta Mode on - so a pilot
 *                                           customer is let in by a toggle, not a deploy.
 *   GA    -> everyone, by the permission table (D-12).
 *
 * Move STAGE down the ladder to promote; nothing else changes. The area reached GA on
 * 2026-09-30 (KY: "go live"): the avatar, the workspace menu and the old /users and
 * /connections routes all lead here now (Lite's /lite/users twin not yet, W-18). The
 * old dialogs are still mounted, but only as the fallback for the kill switch and for
 * builder sessions.
 *
 * `workspaceSettingsV2: false` in webApp.config.json is the kill switch: it fails
 * closed for everyone, staff included, with no rebuild, and everyone is back on the
 * old dialogs and routes.
 */
import { ReleaseStage } from "../commons/enum";
import { authorizationStore } from "../store/authorizationStore";
import { clientStore } from "../store/clientStore";
import { foldedLocation, settingsItem } from "./settingsNav";

/** Not a ReleaseStage: anything the ladder does not name is GA to stageAllows. */
export const SETTINGS_GA = "GA";

// The stage this area is currently at. GA since 2026-09-30; the sections that are not
// finished keep their own stage in settingsNav.js.
export const SETTINGS_STAGE = SETTINGS_GA;

/**
 * The rule itself, free of stores so every stage can be tested.
 *
 * @param {string} stage    one of ReleaseStage, or anything else for GA
 * @param {object} who      { enabled, internal, beta, workspaceBeta }
 */
export function stageAllows(stage, who) {
  const ctx = who || {};
  // The kill switch closes the area for everyone, staff included.
  if (!ctx.enabled) return false;

  switch (stage) {
    case ReleaseStage.ALPHA:
      return Boolean(ctx.internal);
    case ReleaseStage.BETA:
      return Boolean(ctx.beta || ctx.workspaceBeta);
    default:
      // GA: the permission table governs, not the stage.
      return true;
  }
}

/** Who is asking, as the gate reads them. */
function currentUser() {
  return {
    enabled: authorizationStore.getters.appWorkspaceSettings,
    internal: authorizationStore.getters.isInternal,
    beta: authorizationStore.getters.isBeta,
    workspaceBeta: clientStore.getters.workspaceBeta,
  };
}

/** True when the signed-in user is inside the current stage. */
export function settingsStageAllowed() {
  return stageAllows(SETTINGS_STAGE, currentUser());
}

/**
 * One section's gate. A section may declare its own stage so the AREA can move
 * down the ladder while the parts that are not ready stay where they are --
 * People has no table yet, Audit has no endpoint, Export has nothing at all,
 * and none of those should hold back the pages that are finished.
 *
 * The area is the floor, never the ceiling: a section is reachable only if the
 * area lets this person in AND its own stage does. A section marked GA inside
 * an alpha area is still staff-only, which is what "gate the whole thing until
 * it is ready" (W-25) has to keep meaning.
 *
 * @param {string} areaStage     SETTINGS_STAGE
 * @param {string} sectionStage  the item's own stage, or falsy to follow the area
 * @param {object} who           { enabled, internal, beta, workspaceBeta }
 */
export function sectionAllows(areaStage, sectionStage, who) {
  if (!stageAllows(areaStage, who)) return false;
  return sectionStage ? stageAllows(sectionStage, who) : true;
}

/** True when this nav item is reachable by the signed-in user. */
export function settingsSectionAllowed(item) {
  return sectionAllows(SETTINGS_STAGE, item && item.stage, currentUser());
}

/**
 * Where an old route that folded into the frame (settingsNav FOLDED_ROUTES) goes for the
 * signed-in user, or null to serve the old route exactly as it was: it did not fold in, or
 * its section is not open to them -- the kill switch, or a section still on its own stage.
 * The page's role check (D-12) is the frame's, as for any other way in.
 */
export function settingsRedirectFor(routeName) {
  const location = foldedLocation(routeName);
  if (!location) return null;
  return settingsSectionAllowed(settingsItem(location.params.section)) ? location : null;
}

/**
 * The badge the frame shows while this is not GA, as a prop bag for
 * <fts-release-badge> ({} once the area is GA and the badge should disappear).
 */
export function settingsStageBadge() {
  switch (SETTINGS_STAGE) {
    case ReleaseStage.ALPHA:
      return { alpha: true };
    case ReleaseStage.BETA:
      return { stage: ReleaseStage.BETA };
    default:
      return null;
  }
}

export default {
  SETTINGS_GA,
  SETTINGS_STAGE,
  stageAllows,
  sectionAllows,
  settingsStageAllowed,
  settingsSectionAllowed,
  settingsRedirectFor,
  settingsStageBadge,
};
